import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Button from '../../molecules/button/Button';
import Dropdown from '../dropdown/Dropdown';
import styles from './NewButtonGroup.module.css';
import ArrowDownSLineIcon from '../../atoms/icons/ArrowDownSLineIcon';
import { ButtonVariant } from '../../molecules/button/ButtonVariant';
import { classNames } from '../../utils';
import CheckLineIcon from '../../atoms/icons/CheckLineIcon';

export interface ButtonGroupButton {
    text: string;
    selected?: boolean;
    variant: ButtonVariant;
    onClick: () => void;
    icon?: ReactNode;
    className?: string;
    disabled?: boolean;
}

interface NewButtonGroupProps {
    buttons: ButtonGroupButton[];
    dropdownButtons?: ButtonGroupButton[];
    align?: 'left' | 'center' | 'right';
}

const NewButtonGroup: React.FC<NewButtonGroupProps> = ({
    buttons,
    dropdownButtons = [],
    align = 'left'
}) => {
    const dropdownCard = useRef<HTMLDivElement>(null);
    const dropdownContentRef = useRef<HTMLDivElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);
    const [isDropdownActive, setDropdownActive] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            const clickedInsideDropdown =
                dropdownContentRef.current?.contains(target) ||
                dropdownButtonRef.current?.contains(target);

            if (!clickedInsideDropdown) {
                setDropdownActive(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDropdownButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDropdownActive(!isDropdownActive);
    }

    return (
        <div className={classNames(styles.buttonContainer, align && styles[align])}>
            {buttons.map((button, index) => (
                <Button
                    key={index}
                    onClick={button.onClick}
                    variant={button.variant}
                    className={`${styles.buttonGroup} ${button.className}`}
                    disabled={button.disabled}
                >
                    {button.icon}
                    {button.text}
                </Button>
            ))}

            {dropdownButtons.length > 0 && (
                <div className={styles.dropdownWrapper}>
                    <Button
                        ref={dropdownButtonRef}
                        onClick={handleDropdownButtonClick}
                        variant={'secondary-gray'}
                        className={styles.dropdownButton}
                    >
                        <ArrowDownSLineIcon
                            className={`${styles.arrowDownIcon} ${
                                isDropdownActive ? styles.open : styles.closed
                            }`}
                        />
                    </Button>

                    <Dropdown
                        ref={dropdownCard}
                        isActive={isDropdownActive}
                        className={styles.dropdown}
                        align="right"
                    >
                        <div ref={dropdownContentRef}>
                            {dropdownButtons.map((dropdownButton, index) => (
                                <Button
                                    key={index}
                                    variant='secondary-gray'
                                    onClick={dropdownButton.onClick}
                                    className={classNames(
                                        styles.dropdownButtonItem,
                                        dropdownButton.selected && styles.selected,
                                        dropdownButton.className && dropdownButton.className
                                    )}
                                    disabled={dropdownButton.disabled}
                                >
                                    {dropdownButton.icon}
                                    {dropdownButton.text}
                                    {dropdownButton.selected && <CheckLineIcon className={styles.checkLineIcon}/>}
                                </Button>
                            ))}
                        </div>
                    </Dropdown>
                </div>
            )}
        </div>
    );
};

export default NewButtonGroup;
